import { Fragment, type ElementType, type ReactNode } from 'react'
import { useLocation, useMatch } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useLingui } from '@lingui/react/macro'
import { Skeleton } from '@/components/ui/skeleton'
import { SectionLabel } from '@/components/custom/section-label'
import { useRootChrome } from '@/components/custom/route-transition'
import { useSessionUser } from '@/hooks/use-session'
import { useActiveEmployer } from '@/hooks/use-active-employer'
import { usePermissions } from '@/hooks/use-permissions'
import { cn, formatNumber } from '@/lib/utils'
import {
  SETTINGS_FIELDS,
  SETTINGS_HEADER,
  SETTINGS_STACK,
  SKELETON_TILE,
  SettingsSection,
  SettingsSectionSkeleton,
} from './settings-section'
import { SectionHeading, sectionTone } from '../shared/section-heading'
import { Skeleton as TextSkeleton, TextBars } from '../shared/skeleton'
import { SandboxNotice } from '../api/sandbox-notice'
import { PINNED_COLUMN } from '../shared/pane-metrics'
import { GUIDE_SECTIONS, useGuideTitles } from '../api/playground/guide-sections'

// The /account area's pending UIs, each drawn from the markup it stands in for (account.tsx, settings-sidebar.tsx,
// the pages under it), so the real page lands where its skeleton was. Copy the page will show anyway (titles, labels,
// the guide's opening) is set as TextBars, which wrap exactly where it will.

/**
 * The Settings sidebar's column (account.tsx): pinned beside the page on desktop like the sections' list rails,
 * with the same fading divider; the phone gets the tree in a sheet instead.
 */
export const SETTINGS_SIDEBAR_CLASS = `rule-fade-y relative hidden w-64 shrink-0 md:block ${PINNED_COLUMN}`
/** The rest of the width, with the title bar's gutters (wider beside the sidebar). */
export const SETTINGS_CONTENT_CLASS = 'min-w-0 flex-1 px-3 pt-4 pb-8 sm:px-5 md:px-8 md:pt-6'
/** A Settings page's reading width, centred in that space. */
export const SETTINGS_READING_CLASS = 'mx-auto max-w-4xl'

type CachedApp = {
  clientId: string
  name: string | null
  isSandbox: boolean
  candidates: number
  companies: number
  jobs: number
}

/**
 * The integrations the account layout already lists (its loader starts the query), read without waiting: a pending
 * UI draws what is in hand and never suspends. Sandbox last, as the sidebar sorts it.
 */
function useCachedApps(): CachedApp[] | undefined {
  const queryKey = useMatch({
    from: '/_app/account',
    shouldThrow: false,
    select: (match) => match.context.appsQueryOptions?.queryKey,
  })
  const apps = useQueryClient().getQueryData<CachedApp[]>(queryKey ?? ['_'])
  return apps && [...apps].sort((a, b) => Number(a.isSandbox) - Number(b.isSandbox))
}

type IntegrationPage = 'settings' | 'applicants' | 'companies' | 'jobs'
type AccountPage =
  | { kind: 'profile' }
  | { kind: 'billing' }
  | { kind: 'guide' }
  | { kind: 'integration'; appId: string; page: IntegrationPage }

/** Which /account page a pathname opens, the bare section and an integration's bare path as their redirects land. */
function useAccountPage(): AccountPage {
  const pathname = useLocation({ select: (location) => location.pathname.replace(/\/$/, '') })
  const integration = /\/account\/integrations\/([^/]+)(?:\/([a-z]+))?/.exec(pathname)
  if (pathname.endsWith('/account/billing')) return { kind: 'billing' }
  if (pathname.endsWith('/account/integrations') || integration?.[1] === 'guide') return { kind: 'guide' }
  if (integration) {
    const page = (['applicants', 'companies', 'jobs'] as const).find((entity) => entity === integration[2])
    return { kind: 'integration', appId: integration[1] ?? '', page: page ?? 'settings' }
  }
  return { kind: 'profile' }
}

/** Mirrors account.tsx's own choice: a professional's own profile is one page, titled for them, with no sidebar. */
function useAccountChrome() {
  const user = useSessionUser()
  const { isCompanyView } = usePermissions()
  const applicant = user?.type === 'applicant'
  return { sidebar: !applicant, profileData: applicant && !isCompanyView }
}

/** The full /account layout (account.tsx) while its code loads: the title bar, the sidebar, and the page it opens. */
export function SettingsLayoutPending() {
  const chrome = useAccountChrome()
  const page = useAccountPage()
  return (
    <div className="flex h-full flex-col">
      <AccountTitleSkeleton profileData={chrome.profileData} page={page} />
      <div className="scrollbar relative min-h-0 flex-1 max-md:overflow-y-scroll md:overflow-x-clip md:overflow-y-visible">
        <div className="flex min-h-full">
          {chrome.sidebar && (
            <div className={SETTINGS_SIDEBAR_CLASS}>
              <div className="h-full overflow-hidden px-3 py-4 sm:px-5">
                <SettingsSidebarSkeleton page={page} />
              </div>
            </div>
          )}
          <div className={SETTINGS_CONTENT_CLASS}>
            {/* The guide centres its own column (its `fullWidth`); every other page sits in the reading width. */}
            <div className={cn('w-full', page.kind !== 'guide' && SETTINGS_READING_CLASS)}>
              {page.kind === 'profile' ? (
                <ProfilePending />
              ) : page.kind === 'billing' ? (
                <BillingPending />
              ) : (
                <IntegrationsPending />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const CRUMB_TEXT_CLASS = 'truncate text-base font-semibold sm:text-xl'

/**
 * TitleSection with AccountBreadcrumbs in it: the phone's in-pane bar above it (as TitleSection draws it outside a
 * stack), the icon tile, and the trail's own words — or "My profile data" alone for a professional.
 */
function AccountTitleSkeleton(props: { profileData: boolean; page: AccountPage }) {
  const { t } = useLingui()
  const apps = useCachedApps()
  const { page } = props
  const appName =
    page.kind === 'integration' ? apps?.find((app) => app.clientId === page.appId)?.name || null : undefined
  const last =
    page.kind === 'profile' ? (
      t`Profile`
    ) : page.kind === 'billing' ? (
      t`Billing`
    ) : page.kind === 'guide' ? (
      t`Guide`
    ) : appName !== undefined && apps ? (
      (appName ?? t`Unnamed integration`)
    ) : (
      <TextSkeleton chars={12} />
    )
  const crumbs: ReactNode[] = [
    t`Account`,
    ...(page.kind === 'guide' || page.kind === 'integration' ? [t`Integrations`] : []),
    last,
  ]
  return <SettingsTitleSkeleton title={props.profileData ? t`My profile data` : undefined} crumbs={crumbs} />
}

/**
 * TitleSection while its area loads: the phone's in-pane bar above it (as TitleSection draws it outside a stack), the
 * icon tile, and the trail's own words (a string is set as TextBars) — or `title` alone, for an area with no trail.
 */
export function SettingsTitleSkeleton(props: { title?: string; crumbs: ReactNode[] }) {
  const rootChrome = useRootChrome()
  return (
    <>
      {rootChrome}
      <div className="rule-fade-ends bg-background relative z-40 flex shrink-0 items-center gap-0.5 p-3 sm:p-5">
        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
          <Skeleton className="size-8 shrink-0 rounded-lg sm:size-9" />
          {props.title ? (
            <span className={CRUMB_TEXT_CLASS}>
              <TextBars>{props.title}</TextBars>
            </span>
          ) : (
            <ol className="flex min-w-0 flex-nowrap items-center gap-1.5 text-sm">
              {props.crumbs.map((crumb, index) => (
                // oxlint-disable-next-line react/no-array-index-key -- a fixed trail
                <Fragment key={index}>
                  {index > 0 && (
                    <li className="flex translate-y-px items-center">
                      <Skeleton className="size-3.5 rounded-sm" />
                    </li>
                  )}
                  <li className="inline-flex min-w-0 items-center gap-1">
                    <span className={CRUMB_TEXT_CLASS}>
                      {typeof crumb === 'string' ? <TextBars>{crumb}</TextBars> : crumb}
                    </span>
                    {/* On a phone the last crumb opens the tree, and carries the sheet's chevron. */}
                    {index === props.crumbs.length - 1 && <Skeleton className="size-4 shrink-0 rounded-sm md:hidden" />}
                  </li>
                </Fragment>
              ))}
            </ol>
          )}
        </div>
      </div>
    </>
  )
}

// settings-sidebar.tsx's row.
const ROW = 'flex min-w-0 items-center gap-2 rounded-md px-2 py-1.5 text-sm'

export function SidebarRowSkeleton(props: {
  lead: ReactNode
  label: ReactNode
  trail?: ReactNode
  here?: boolean
  className?: string
}) {
  return (
    <div className={cn(ROW, props.here && 'bg-muted/60 font-medium', props.className)}>
      {props.lead}
      <span className="min-w-0 flex-1 truncate">{props.label}</span>
      {props.trail}
    </div>
  )
}

export const ROW_ICON_SKELETON = <Skeleton className="size-4 shrink-0 rounded" />
export const ROW_CHEVRON_SKELETON = <Skeleton className="size-3.5 shrink-0 rounded-sm" />

/**
 * The Settings tree (settings-sidebar.tsx) as it will open on this page: Profile and Billing, the integrations (the
 * one holding the page unfolded to its four pages), and the guide, unfolded to its sections on the guide.
 */
function SettingsSidebarSkeleton(props: { page: AccountPage }) {
  const { t, i18n } = useLingui()
  const apps = useCachedApps()
  const titles = useGuideTitles()
  const { page } = props
  const pageLabel = { settings: t`Settings`, applicants: t`Candidates`, companies: t`Companies`, jobs: t`Jobs` }
  return (
    <div className="flex flex-col gap-0.5">
      <SidebarRowSkeleton
        lead={ROW_ICON_SKELETON}
        label={<TextBars>{t`Profile`}</TextBars>}
        here={page.kind === 'profile'}
      />
      <SidebarRowSkeleton
        lead={ROW_ICON_SKELETON}
        label={<TextBars>{t`Billing`}</TextBars>}
        here={page.kind === 'billing'}
      />
      <div className="mt-4 flex items-center justify-between pr-1 pl-2">
        <SectionLabel>
          <TextBars>{t`Your integrations`}</TextBars>
        </SectionLabel>
        <span className="flex size-6 items-center justify-center">
          <Skeleton className="size-3.5 rounded-sm" />
        </span>
      </div>
      {apps?.map((app) => {
        const open = page.kind === 'integration' && page.appId === app.clientId
        const counts = { settings: null, applicants: app.candidates, companies: app.companies, jobs: app.jobs }
        return (
          <div key={app.clientId} className="flex flex-col">
            <SidebarRowSkeleton
              lead={<Skeleton className="mx-1 size-2 shrink-0 rounded-full" />}
              label={<TextBars>{app.name || t`Unnamed integration`}</TextBars>}
              trail={ROW_CHEVRON_SKELETON}
              className={cn(open && 'font-medium')}
            />
            {open && (
              <div className="ml-4 flex flex-col gap-0.5 pt-0.5">
                {(['settings', 'applicants', 'companies', 'jobs'] as const).map((key) => (
                  <SidebarRowSkeleton
                    key={key}
                    lead={ROW_ICON_SKELETON}
                    label={<TextBars>{pageLabel[key]}</TextBars>}
                    here={page.page === key}
                    trail={
                      counts[key] != null && (
                        <span className="text-xs tabular-nums">
                          <TextBars>{formatNumber(i18n.locale, counts[key], 0)}</TextBars>
                        </span>
                      )
                    }
                  />
                ))}
              </div>
            )}
          </div>
        )
      })}
      <div className="flex flex-col">
        <SidebarRowSkeleton
          lead={ROW_ICON_SKELETON}
          label={<TextBars>{t`Guide`}</TextBars>}
          trail={ROW_CHEVRON_SKELETON}
          className={cn(page.kind === 'guide' && 'font-medium')}
        />
        {page.kind === 'guide' && (
          <ol className="mt-0.5 ml-4 flex flex-col gap-0.5 pl-px">
            {GUIDE_SECTIONS.map((id, index) => (
              // The row's checkbox lays over its start padding, as InPageSections draws it.
              <li key={id} className={cn(ROW, 'relative items-baseline pl-8')}>
                <Skeleton className="absolute top-2 left-2 size-4 rounded-full" />
                <span className="min-w-0">
                  <TextBars>{`${index + 1}. ${titles[id]}`}</TextBars>
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  )
}

/**
 * The profile while it loads, in the shape of the variant the page picks (account.index.tsx: same choice, same order):
 * each section's card, with its field rows or its one-line body. The profile photo card is drawn as it is.
 */
export function ProfilePending() {
  const user = useSessionUser()
  const { activeEmployerId, isEmployee } = useActiveEmployer()
  const { isCompanyView } = usePermissions()
  const signIn = (
    <>
      <SettingsSectionSkeleton rows={1} />
      <SettingsSectionSkeleton>
        <div className="h-[115px] rounded-xl border" />
      </SettingsSectionSkeleton>
      <SettingsSectionSkeleton line />
    </>
  )
  const photo = (
    <SettingsSectionSkeleton>
      <div className="flex flex-wrap items-center gap-4">
        <Skeleton className="size-16 shrink-0 rounded-lg" />
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <Skeleton className="h-4 w-32 max-w-full" />
          <Skeleton className="h-4 w-48 max-w-full" />
        </div>
        <Skeleton className="h-7 w-32 rounded-lg" />
      </div>
    </SettingsSectionSkeleton>
  )
  if (isEmployee && activeEmployerId !== null)
    return isCompanyView ? (
      <div className={SETTINGS_STACK}>
        <SettingsSectionSkeleton rows={[32, 34, 34, 32, 32, 32, 34]} />
        <SettingsSectionSkeleton rows={3} />
      </div>
    ) : (
      <div className={SETTINGS_STACK}>
        <SettingsSectionSkeleton rows={5} />
        <SettingsSectionSkeleton rows={2} />
        <SettingsSectionSkeleton rows={5} />
        <SettingsSectionSkeleton rows={4} />
      </div>
    )
  if (user?.type === 'applicant')
    return (
      <div className={SETTINGS_STACK}>
        {photo}
        <SettingsSectionSkeleton rows={[32, 32, 32, 32, 34, 34, 32, 32]} />
        <SettingsSectionSkeleton rows={[32, 32, 32, 32, 34, 34]} />
        {signIn}
        <SettingsSectionSkeleton danger line />
      </div>
    )
  return (
    <div className={SETTINGS_STACK}>
      {photo}
      <SettingsSectionSkeleton rows={[32, 34, 34, 32, 32, 32, 34]} />
      <SettingsSectionSkeleton rows={3} />
      {signIn}
      <SettingsSectionSkeleton line />
      <SettingsSectionSkeleton rows={1} />
      <SettingsSectionSkeleton danger line />
    </div>
  )
}

export function BillingPending() {
  return (
    <div className={SETTINGS_STACK}>
      <BillingPlanSkeleton />
      <SettingsSectionSkeleton rows={[32, 32, 32, 34, 34, 32, 32, 32]} />
    </div>
  )
}

type CachedBilling = { subscription: { plan: string }; plans: unknown[] }

/**
 * BillingPlan while it loads: the current plan's card and, on the free plan, the plans on offer. Which plan, and how
 * many offers, come from the billing query when it is already in hand; otherwise the free plan with one offer, which
 * is where every account starts. As the route's pending it draws no glyphs, so the Settings icons stay off the eager
 * path; as BillingPlan's own fallback it takes them, with the sections' hue.
 */
export function BillingPlanSkeleton(props: { icons?: { plan: ElementType; plans: ElementType } }) {
  const { t } = useLingui()
  const queryKey = useMatch({
    from: '/_app/account/billing',
    shouldThrow: false,
    select: (match) => match.context.billingQueryOptions?.queryKey,
  })
  const billing = useQueryClient().getQueryData<CachedBilling>(queryKey ?? ['_'])
  const offers = billing ? (billing.subscription.plan === 'free' ? billing.plans.length : 0) : 1
  const tone = props.icons ? sectionTone('amber') : SKELETON_TILE
  return (
    <>
      <SettingsSection
        icon={props.icons?.plan ?? null}
        tone={tone}
        title={t`Current plan`}
        description={t`Your subscription details and usage`}
      >
        {/* BillingPlan's own body at its measured heights: the plan's name, the three usage cards (stacked on a
            phone), and the plan's dates (wrapping to two lines on a phone). */}
        <div className="flex flex-col gap-6">
          <h4 className="text-2xl font-semibold tracking-tight">
            <TextSkeleton chars={8} />
          </h4>
          <div className="grid gap-4 sm:grid-cols-3">
            {[t`Reports consumed`, t`Consumed comparisons`, t`Market profiles`].map((label) => (
              <div key={label} className="bg-background flex h-[105px] flex-col gap-2 rounded-xl border p-4">
                <span className="text-muted-foreground text-[10px] font-semibold tracking-wider uppercase">
                  {label}
                </span>
                <span className="text-3xl font-semibold tracking-tight">
                  <TextSkeleton chars={4} />
                </span>
              </div>
            ))}
          </div>
          <Skeleton className="h-24 rounded-lg sm:h-[52px]" />
        </div>
      </SettingsSection>
      {offers > 0 && (
        <SettingsSection
          icon={props.icons?.plans ?? null}
          tone={tone}
          title={t`Plans available`}
          description={t`Upgrade your plan to unlock more features`}
        >
          <div className="grid gap-4 sm:grid-cols-[repeat(auto-fit,minmax(15rem,1fr))]">
            {Array.from({ length: offers }, (_, index) => (
              <div key={index} className="bg-background flex flex-col gap-4 rounded-2xl border p-5">
                <div className="flex flex-col gap-1">
                  <h4 className="font-semibold">
                    <TextSkeleton chars={10} />
                  </h4>
                  <p className="text-3xl font-semibold tracking-tight">
                    <TextSkeleton chars={7} />
                  </p>
                </div>
                <ul className="flex flex-col gap-2 border-t pt-4 text-sm">
                  {[30, 28, 26].map((chars) => (
                    <li key={chars} className="flex items-start gap-2">
                      <Skeleton className="mt-0.5 size-4 shrink-0 rounded-sm" />
                      <TextSkeleton chars={chars} />
                    </li>
                  ))}
                </ul>
                <div className="text-sm font-medium">
                  <TextBars>{t`Select plan`}</TextBars>
                </div>
              </div>
            ))}
          </div>
        </SettingsSection>
      )}
    </>
  )
}

/** A `size="sm"` secondary Button (button.tsx) with its icon, its real label set invisibly in the button's own type. */
function SmallActionSkeleton(props: { label: string; icon?: 'leading' | 'leading-lg' | 'trailing' }) {
  const icon = <span className={cn('shrink-0', props.icon === 'leading-lg' ? 'size-4' : 'size-3.5')} />
  return (
    <Skeleton className="inline-flex h-7 shrink-0 items-center gap-1 rounded-[min(var(--radius-md),12px)] px-2.5 text-[0.8rem] font-medium whitespace-nowrap">
      {props.icon !== 'trailing' && icon}
      <span className="invisible">{props.label}</span>
      {props.icon === 'trailing' && icon}
    </Skeleton>
  )
}

/**
 * The guide while it loads (tutorial.tsx's header, in its classes): the title and actions, the two opening paragraphs
 * and the sandbox callout as their own words, then the sandbox's credentials, or the card that creates it.
 */
export function GuidePending() {
  const { t } = useLingui()
  const sandbox = useCachedApps()?.some((app) => app.isSandbox)
  return (
    <div className="pt-4 sm:pt-6">
      <div className="mx-auto flex w-full max-w-prose min-w-0 flex-col gap-12">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col items-start gap-4">
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              <TextBars>{t`Integrate D’Anchiano into your platform`}</TextBars>
            </h1>
            <div className="flex flex-wrap items-center gap-2">
              <SmallActionSkeleton label={t`Copy for LLM`} icon="leading" />
              {sandbox && (
                <>
                  <SmallActionSkeleton label={t`Reset playground`} icon="leading-lg" />
                  <SmallActionSkeleton label={t`Sandbox settings`} icon="leading-lg" />
                </>
              )}
              <SmallActionSkeleton label={t`API reference`} icon="trailing" />
            </div>
          </div>
          <p className="max-w-prose text-[15px] leading-7">
            <TextBars>{t`This page walks through our API in the order an integration uses it: create a company, candidates and a job position, score the candidates against the position, and show the results in your platform. Each step explains what it's for, shows the request and runs it.`}</TextBars>
          </p>
          <p className="max-w-prose text-[15px] leading-7">
            <TextBars>{t`Every screen the API links to can also live inside your own platform: pick “Embed in your platform” on it for the SDK code and a live preview of the screen embedded in a made-up HR tool.`}</TextBars>
          </p>
          <div className="flex max-w-prose flex-col gap-2 rounded-xl border p-4">
            {/* A <div>, not the callout's <p>: the icon's placeholder is a <div>. */}
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Skeleton className="size-4 shrink-0 rounded-sm" />
              <TextBars>{t`Everything here runs in your own sandbox`}</TextBars>
            </div>
            <div className="flex flex-col gap-2 text-sm leading-6">
              <p>
                <TextBars>{t`Nothing here is a mock-up: every example really creates, changes and deletes data, in your sandbox. The sandbox is a private test integration with its own settings. Its data is marked as test data, so it never reaches the market statistics or anyone's lists, and Reset playground wipes all of it.`}</TextBars>
              </p>
              <p>
                <TextBars>{t`The examples use the sandbox's real API key, so your developers can copy any of them and run it outside this page. That key only works with the sample company and candidates this guide uses, and it cannot sign in to your account.`}</TextBars>
              </p>
            </div>
          </div>
          {sandbox ? (
            <div className="flex max-w-prose flex-col gap-4">
              {[t`Client ID`, t`Secret key (sandbox)`].map((label) => (
                <div key={label} className="flex flex-col gap-2">
                  <span className="text-xs leading-none font-medium">
                    <TextBars>{label}</TextBars>
                  </span>
                  <Skeleton className="h-11 rounded-lg" />
                </div>
              ))}
              <div>
                <SmallActionSkeleton label={t`Regenerate secret`} icon="leading" />
              </div>
            </div>
          ) : (
            <div className="flex max-w-prose flex-col gap-3 rounded-2xl border p-5">
              <div className="flex flex-col gap-1">
                <p className="font-semibold">
                  <TextBars>{t`Create your sandbox to start`}</TextBars>
                </p>
                <p className="text-sm">
                  <TextBars>{t`A private test integration of your own, with an API key that only reaches sample data. Every example on this page runs in it.`}</TextBars>
                </p>
              </div>
              <Skeleton className="h-8 w-fit rounded-lg px-2.5 text-sm font-medium">
                <span className="invisible">{t`Create your sandbox`}</span>
              </Skeleton>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * An integration's list (integration-list.tsx) while it loads: the same card, its heading's words and the integration's
 * name, the search row, and as many rows as the page will hold (its total from the sidebar's counts, eight to a page)
 * at the rows' own geometry (Item xs, a size-8 avatar) — or the empty state when there are none.
 */
export function IntegrationListPending() {
  const { t, i18n } = useLingui()
  const page = useAccountPage()
  const app = useCachedApps()?.find((cached) => page.kind === 'integration' && cached.clientId === page.appId)
  const entity = page.kind === 'integration' && page.page !== 'settings' ? page.page : 'applicants'
  const total = app?.[entity === 'applicants' ? 'candidates' : entity]
  const rows = total === undefined ? 5 : Math.min(total, 8)
  // The page's own words (the list routes'): its heading, and its empty state's title and line.
  const copy = {
    applicants: {
      title: t`Candidates`,
      empty: t`No candidates yet`,
      line: t`Candidates your integration creates or links through the API show here.`,
    },
    companies: {
      title: t`Companies`,
      empty: t`No companies yet`,
      line: t`Companies your integration creates or links through the API show here.`,
    },
    jobs: {
      title: t`Jobs`,
      empty: t`No jobs yet`,
      line: t`Positions your integration creates through the API show here.`,
    },
  }[entity]
  return (
    <div className={SETTINGS_STACK}>
      {app?.isSandbox && (
        // SandboxResetHint (entity-row-actions.tsx), in its box and words.
        <div className="bg-muted/50 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm">
          <Skeleton className="size-4 shrink-0 rounded-sm" />
          <span>
            <TextBars>{t`These are the playground tutorial's sample rows. Reset the playground to remove all of them at once.`}</TextBars>{' '}
            <span className="font-medium">
              <TextBars>{t`Open the guide`}</TextBars>
            </span>
          </span>
        </div>
      )}
      <div className="bg-card overflow-hidden rounded-2xl border">
        <div className={SETTINGS_HEADER}>
          <SectionHeading
            icon={null}
            tone={SKELETON_TILE}
            title={
              <TextBars>
                {copy.title}
                {total !== undefined && (
                  <span className="ml-2.5 font-normal tabular-nums">{formatNumber(i18n.locale, total, 0)}</span>
                )}
              </TextBars>
            }
            description={app ? <TextBars>{app.name || t`Unnamed integration`}</TextBars> : <TextSkeleton chars={18} />}
          />
        </div>
        <div className="border-b px-6 pt-6 pb-4">
          <Skeleton className="h-8 w-full rounded-lg" />
        </div>
        <div className="flex flex-col gap-0.5 px-4 py-2">
          {rows === 0 ? (
            // PanelEmptyState: the round icon over its title and line.
            <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
              <Skeleton className="size-11 rounded-full" />
              <div className="flex flex-col gap-1">
                <p className="text-md font-medium">
                  <TextBars>{copy.empty}</TextBars>
                </p>
                <p className="text-sm">
                  <TextBars>{copy.line}</TextBars>
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* The count label (SectionLabel: 10px text in py-1.5). */}
              <div className="px-2 py-1.5">
                <Skeleton className="h-[15px] w-20" />
              </div>
              <div className="flex flex-col gap-0.5">
                {Array.from({ length: rows }, (_, i) => (
                  // The row's two lines at their own line boxes: the title's 20px and the meta line's 16px.
                  <div key={i} className="flex items-center gap-2 px-2.5 py-2">
                    <Skeleton className="size-8 shrink-0 rounded-md" />
                    <div className="flex min-w-0 flex-1 flex-col">
                      <div className="flex h-5 items-center">
                        <Skeleton className="h-3.5 w-40 max-w-full" />
                      </div>
                      <div className="flex h-4 items-center">
                        <Skeleton className="h-3 w-56 max-w-full" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * An integration's Settings page while it loads: the same cards in the integrations' hue (credentials, appearance,
 * domains, callbacks) and the danger zone, over the sandbox notice when this client is the sandbox.
 */
export function IntegrationSettingsPending(props: { sandbox?: boolean }) {
  const { t } = useLingui()
  return (
    <div className={SETTINGS_STACK}>
      {props.sandbox && <SandboxNotice />}
      {/* Each card's heading in its own words, and its body at its measured heights (the hints and the danger line
          wrap on a phone). */}
      <SettingsSectionSkeleton
        title={t`API Credentials`}
        description={t`Use these credentials to authenticate your API requests. Keep your Client Secret secure.`}
      >
        <div className={SETTINGS_FIELDS}>
          {[0, 1].map((i) => (
            <div key={i} className="flex flex-col gap-2">
              <Skeleton className="h-[15px] w-24" />
              <Skeleton className="h-11 rounded-lg" />
            </div>
          ))}
          <Skeleton className="h-7 w-40 rounded-lg" />
          <Skeleton className="h-[65px] rounded-lg" />
        </div>
      </SettingsSectionSkeleton>
      <SettingsSectionSkeleton
        title={t`Appearance & Branding`}
        description={t`Customize how assessments appear when embedded in your application.`}
      >
        <div className={SETTINGS_FIELDS}>
          {/* The name and colour inputs, then the theme's segmented control, which is only as wide as its three options. */}
          {[
            { height: 32, width: undefined },
            { height: 32, width: undefined },
            { height: 30, width: 212 },
          ].map((field, i) => (
            // oxlint-disable-next-line react/no-array-index-key -- a fixed placeholder list
            <div key={i} className="flex flex-col gap-2">
              <Skeleton className="h-5 w-28" />
              <Skeleton className="rounded-lg" style={field} />
            </div>
          ))}
        </div>
      </SettingsSectionSkeleton>
      <SettingsSectionSkeleton
        title={t`Security & Allowed Domains`}
        description={t`Restrict API access to specific domains. Leave empty to allow requests from any origin.`}
      >
        <Skeleton className="h-[127px] rounded-lg sm:h-[113px]" />
      </SettingsSectionSkeleton>
      <SettingsSectionSkeleton
        title={t`Integration Callbacks`}
        description={t`Configure redirect URLs and button behavior for seamless integration.`}
      >
        <div className={SETTINGS_FIELDS}>
          <Skeleton className="h-[99px] rounded-lg sm:h-[83px]" />
          <Skeleton className="h-[99px] rounded-lg sm:h-[83px]" />
          <Skeleton className="h-[29px] rounded-lg" />
        </div>
      </SettingsSectionSkeleton>
      <SettingsSectionSkeleton
        danger
        title={t`Delete API Client`}
        description={t`Permanently remove this client and revoke all access. This cannot be undone.`}
      >
        <Skeleton className="h-[51px] rounded-lg sm:h-[37px]" />
      </SettingsSectionSkeleton>
    </div>
  )
}

/** The settings route's pending: whether this client is the sandbox comes from the list the layout already holds. */
export function IntegrationSettingsRoutePending() {
  const page = useAccountPage()
  const apps = useCachedApps()
  return (
    <IntegrationSettingsPending
      sandbox={apps?.some((app) => page.kind === 'integration' && app.clientId === page.appId && app.isSandbox)}
    />
  )
}

/** The Integrations layout's pending: the guide's, or an integration's page (settings or a list). */
export function IntegrationsPending() {
  const page = useAccountPage()
  if (page.kind === 'integration')
    return page.page === 'settings' ? <IntegrationSettingsRoutePending /> : <IntegrationListPending />
  return <GuidePending />
}
